import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

/**
 * HelpModal Component
 *
 * A reusable modal component for displaying comprehensive help documentation.
 * Uses Bootstrap modals for consistent styling and behavior.
 *
 * @param {Object} props
 * @param {string} props.id - Unique ID for the modal
 * @param {string} props.title - Modal title
 * @param {React.ReactNode} props.children - Modal content
 * @param {string} [props.size='lg'] - Modal size (sm, md, lg, xl)
 */
function HelpModal({ id, title, children, size = 'lg' }) {
  const modalRef = useRef(null)

  useEffect(() => {
    // Ensure modal backdrop is properly managed
    return () => {
      // Cleanup any lingering backdrops on unmount
      const backdrops = document.querySelectorAll('.modal-backdrop')
      backdrops.forEach((backdrop) => backdrop.remove())
      document.body.classList.remove('modal-open')
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
    }
  }, [])

  return (
    <div
      className="modal fade"
      id={id}
      tabIndex="-1"
      aria-labelledby={`${id}-label`}
      aria-hidden="true"
      ref={modalRef}
    >
      <div className={`modal-dialog modal-${size} modal-dialog-scrollable`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id={`${id}-label`}>
              {title}
            </h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">{children}</div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

HelpModal.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
}

export default HelpModal
